import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, Card } from "@mui/material";
import {
  AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";

import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const DocumentoBien = ({ bien, procesoId, identificador, setError }) => {
  const [documentoUrl, setDocumentoUrl] = useState("");
  const [hasDocumento, setHasDocumento] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  useEffect(() => {
    const checkDocumento = async () => {
      if (!identificador || !procesoId) return;
      try {
        // Verificar existencia del archivo en el bucket
        const response = await BucketApi.checkSolicitudFileExists(
          getDocumentoFileName(),
          identificador
        );
        setHasDocumento(response.data.exists);
        if (response.data.exists) {
          //const urlResponse = await BucketApi.getSolicitudFileUrl(getDocumentoFileName(), identificador);
          const urlResponse = await BucketApi.getSolicitudFileUrlFresh(
            getDocumentoFileName(),
            identificador
          );
          setDocumentoUrl(urlResponse.data.file_url);
        }
      } catch (error) {
        console.error("Error verificando documento:", error);
        setHasDocumento(false);
      }
    };

    checkDocumento();
  }, [identificador, procesoId, bien]);

  const normalizarTexto = (texto, tipo = "lower") => {
    if (!texto) return "";
    const base = texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Elimina tildes
      .replace(/ñ/g, "n") // Reemplaza ñ
      .replace(/Ñ/g, "N") // Reemplaza Ñ
      .replace(/\s+/g, "_"); // Reemplaza espacios

    return tipo === "upper" ? base.toUpperCase() : base.toLowerCase();
  };

  // const normalizarNombre = (texto, tipo = "lower") => {
  //   if (!texto) return "";
  //   const base = texto
  //     .normalize("NFD")
  //     .replace(/\s+/g, "_"); // Reemplaza espacios

  //   return tipo === "upper" ? base.toUpperCase() : base.toLowerCase();
  // };

  // Genera el nombre del archivo PDF
  const getDocumentoFileName = () => {
    const base =
      bien.tipo_bien === "INMUEBLE"
        ? normalizarTexto(bien.matricula_inmobiliaria)
        : normalizarTexto(bien.placa);
    const prefijo = bien.tipo_bien === "INMUEBLE" ? "certificado_tradicion" : "tarjeta_propiedad";
    return `${prefijo}_${base}.pdf`;
  };

  const getNombreArchivo = () => {
    const base = bien.tipo_bien === "INMUEBLE" ? bien.matricula_inmobiliaria : bien.placa;
    const prefijo = bien.tipo_bien === "INMUEBLE" ? "Certificado Tradición" : "Tarjeta Propiedad";
    return `${prefijo} ${base}.pdf`;
  };

  // Genera el tipo de anexo
  const getTipoAnexo = () => {
    const base =
      bien.tipo_bien === "INMUEBLE"
        ? normalizarTexto(bien.matricula_inmobiliaria, "upper")
        : normalizarTexto(bien.placa, "upper");
    const prefijo = bien.tipo_bien === "INMUEBLE" ? "CERTIFICADO_TRADICION" : "TARJETA_PROPIEDAD";
    return `${prefijo}_${base}`;
  };

  const getDocumentoLabel = () => {
    if (bien.tipo_bien === "INMUEBLE") {
      return {
        title: "Certificado de Tradición y Libertad",
        identifier: `${bien.tipo_inmueble.toUpperCase()} -  Matrícula: ${
          bien.matricula_inmobiliaria
        }`,
      };
    }
    return {
      title: "Tarjeta de Propiedad",
      identifier: `${bien.tipo_mueble.toUpperCase()} -  Placa: ${bien.placa}`,
    };
  };

  const handleDocumentoChange = async (event) => {
    const file = event.target.files[0];
    console.log("File selected:", file);
    console.log("Datos:", {
      identificador,
      procesoId,
      tipoAnexo: getTipoAnexo(),
      fileName: getDocumentoFileName(),
    });

    if (!file || !identificador || !procesoId) {
      setError("Faltan datos requeridos para subir el documento");
      console.log("Faltan datos requeridos para subir el documento:", {
        hasFile: !!file,
        hasIdentificador: !!identificador,
        hasProcesoId: !!procesoId,
      });
      return;
    }
    setIsLoading(true);
    try {
      if (hasDocumento) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
          tipo_anexo: getTipoAnexo(),
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
        }
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", getDocumentoFileName());
      formData.append("file_type", "solicitud");
      formData.append("proceso_id", identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", getTipoAnexo());
      formData.append("nombre_anexo", getNombreArchivo());
      formData.append("incluir_en_pdf", "True");
      console.log("Enviando formData:", {
        fileName: getDocumentoFileName(),
        tipoAnexo: getTipoAnexo(),
        identificador,
        registrarAnexo: "true",
      });
      const response = await BucketApi.uploadFile(formData);
      console.log("Respuesta:", response.data);
      if (response.data?.file_url) {
        // Verificar si se registró el anexo
        const anexoResponse = await ProcesosApi.verificarAnexoProceso(procesoId, {
          tipo_anexo: getTipoAnexo(),
        });
        console.log("Verificación de anexo:", anexoResponse.data);
        setHasDocumento(true);
        setDocumentoUrl(response.data.file_url);
      }
    } catch (error) {
      console.error("Error subiendo documento:", error);
      setError(`Error al subir el documento del bien ${getDocumentoLabel().identifier}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocumento = async () => {
    if (!identificador || !window.confirm("¿Está seguro(a) de eliminar el documento?")) return;
    setIsLoading(true);
    try {
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
        tipo_anexo: getTipoAnexo(),
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
      }
      setHasDocumento(false);
      setDocumentoUrl("");
    } catch (error) {
      console.error("Error eliminando documento:", error);
      setError(`Error al eliminar el documento del bien ${getDocumentoLabel().identifier}`);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const documentoLabel = getDocumentoLabel();

  return (
    <Card>
      <SuiBox p={2}>
        <SuiBox mb={2}>
          <SuiTypography variant="h6" fontWeight="medium">
            {documentoLabel.title}
          </SuiTypography>
          <SuiTypography variant="button" fontWeight="regular" color="text">
            {documentoLabel.identifier}
          </SuiTypography>
        </SuiBox>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {hasDocumento ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 150,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1" color="textSecondary" align="center">
                Documento cargado
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "4px",
                  borderRadius: "4px",
                }}
              >
                <IconButton size="small" onClick={triggerFileInput} color="info">
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={handleDeleteDocumento} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: "pointer", mt: 2 }}
                onClick={() => setShowPdfViewer(true)}
              >
                Ver documento
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={triggerFileInput}
              sx={{
                width: "100%",
                height: 150,
                border: "2px dashed #ccc",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#f8f9fa",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "#f0f0f0",
                },
              }}
            >
              <AddAPhotoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography color="textSecondary" align="center" sx={{ px: 2 }}>
                Click para subir el documento
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Formato requerido: PDF
              </Typography>
            </Box>
          )}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleDocumentoChange}
          style={{ display: "none" }}
        />

        {isLoading && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography color="textSecondary">Procesando...</Typography>
          </Box>
        )}

        {/* Modal para visualizar PDF */}
        <FileDialog
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          title={documentoLabel.title}
          content={
            <embed
              src={documentoUrl}
              type="application/pdf"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />
      </SuiBox>
    </Card>
  );
};

DocumentoBien.propTypes = {
  bien: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tipo_bien: PropTypes.oneOf(["INMUEBLE", "MUEBLE"]).isRequired,
    tipo_inmueble: PropTypes.string,
    tipo_mueble: PropTypes.string,
    matricula_inmobiliaria: PropTypes.string,
    placa: PropTypes.string,
  }).isRequired,
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string,
  setError: PropTypes.func.isRequired,
};

export default DocumentoBien;
